import { createFileRoute } from "@tanstack/react-router";
import { RelatedKnowledgeAuto } from "@/components/site/RelatedKnowledgeAuto";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, serviceSchema, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad är produktvisualisering?",
    a: "Produktvisualisering är fotorealistiska bilder och filmer av en produkt som byggs i 3D i stället för att fotograferas. Ni får produktbilder, rotationer, sprängskisser och genomskärningar utan studio, prototyp eller frakt.",
  },
  {
    q: "Vad kostar 3D-produktvisualisering?",
    a: "Still-renders av en befintlig CAD-modell ligger typiskt på 8 000–25 000 kr per vy. En kort produktloop på 15–30 sekunder landar på 40 000–90 000 kr. Teknisk förklaringsfilm med sprängskiss och voice-over kostar 120 000–280 000 kr.",
  },
  {
    q: "Behöver ni CAD-filer?",
    a: "Nej, men det går snabbare med dem. Vi tar emot STEP, IGES, OBJ, SolidWorks och Rhino. Saknas CAD bygger vi modellen från ritningar, mått eller foton — det tillkommer typiskt 20 000–80 000 kr.",
  },
  {
    q: "Hur lång tid tar en produktvisualisering?",
    a: "Still-renders: 1–2 veckor från godkänd CAD. Produktloop: 3–4 veckor. Teknisk animation med flera scener: 6–8 veckor. Vi visar gråa testrenders innan ljussättning så ni kan styra tidigt.",
  },
  {
    q: "Kan ni visa produktvarianter utan ny inspelning?",
    a: "Ja. När modellen väl finns byter vi färg, material, dekaler och konfiguration till en bråkdel av kostnaden. Det är det vanligaste skälet till att bolag med många produktvarianter går från fotostudio till 3D.",
  },
  {
    q: "Får vi filerna för framtida användning?",
    a: "Ni får master-filmer, still-renders i tryckupplösning och bildmaterial för sajt och säljmaterial. Källfiler och scener sparas så att revisioner och nya varianter går snabbt att göra.",
  },
];

const TITLE = "Produktvisualisering i 3D — renders från er CAD | Fenny";
const DESCRIPTION =
  "3D-produktvisualisering för industri, medtech och B2B: fotorealistiska renders, produktloopar och sprängskisser från er CAD. Pris, tidplan och filformat.";

export const Route = createFileRoute("/3d-produktvisualisering")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: "Produktvisualisering i 3D — Fenny" },
      {
        property: "og:description",
        content:
          "Fotorealistiska produktrenders och 3D-produktion från era CAD-filer — för industri, medtech och B2B.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/3d-produktvisualisering" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/3d-produktvisualisering" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          aeoGeo({ path: "/3d-produktvisualisering", name: TITLE, description: DESCRIPTION }),
        ),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceSchema({
            name: "3D-produktvisualisering",
            path: "/3d-produktvisualisering",
            serviceType: "Produktvisualisering och 3D-rendering",
            description:
              "Fotorealistisk produktvisualisering, produktrendering och teknisk 3D-animation från CAD för industri, medtech och B2B.",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "3D-animation", path: "/3d-animation" },
            { name: "Produktvisualisering", path: "/3d-produktvisualisering" },
          ]),
        ),
      },
    ],
  }),
  component: () => (
    <>
      <LandingPage
        eyebrow="Produktvisualisering"
        title={<>Produktvisualisering i 3D — er produkt, innan den finns i verkligheten.</>}
        lede="Vi bygger fotorealistiska renders, produktloopar och sprängskisser från era CAD-filer. Ingen studio, ingen prototyp, ingen frakt — och nya produktvarianter kostar en bråkdel av en ny fotografering."
        tldr={
          <p>
            Produktvisualisering är 3D-renderade bilder och filmer av en produkt. Fenny tar emot{" "}
            <strong>STEP, IGES, OBJ, SolidWorks och Rhino</strong> och levererar still-renders,
            rotationer, sprängskisser och genomskärningar för sajt, säljmaterial, mässa och
            kampanj. Saknas CAD bygger vi modellen från ritning eller foto.
          </p>
        }
        ctaPrimary={{ label: "Skicka er CAD", to: "/kontakt" }}
        ctaSecondary={{ label: "3D-animation", to: "/3d-animation" }}
        trackingId="3d-produktvisualisering"
        relatedLinksFor="/3d-produktvisualisering"
        trust={[
          { value: "1–2 v", label: "till första renders" },
          { value: "STEP/IGES/OBJ", label: "format vi tar emot" },
          { value: "4K + tryck", label: "leveransupplösning" },
        ]}
        pricing={{
          heading: "Vad produktvisualisering kostar",
          intro:
            "Riktpriser 2026. Priset styrs av modellens komplexitet och om CAD finns eller måste byggas.",
          rows: [
            {
              format: "Still-renders",
              scope: "Befintlig CAD, produktbilder i vald vinkel, vit eller enkel miljö",
              price: "8 000–25 000 kr per vy",
              lead: "1–2 veckor",
            },
            {
              format: "Produktloop",
              scope: "15–30 sek rotation och närbilder, en miljö, alla kanalformat",
              price: "40 000–90 000 kr",
              lead: "3–4 veckor",
            },
            {
              format: "Teknisk visualisering",
              scope: "Sprängskiss, genomskärning, funktionsförklaring, voice-over och textning",
              price: "120 000–280 000 kr",
              lead: "6–8 veckor",
            },
          ],
          footnote:
            "Exkl. moms. Saknas CAD tillkommer modellering från ritning eller foto, typiskt 20 000–80 000 kr.",
        }}
        bullets={[
          {
            title: "CAD in, färdigt material ut",
            body: "STEP, IGES, OBJ, SolidWorks och Rhino. Vi optimerar för render utan att tappa precision.",
          },
          {
            title: "Varianter nästan gratis",
            body: "Färg, material, dekal och konfiguration byts i samma scen — inga nya fotograferingar.",
          },
          {
            title: "Bilder och film samtidigt",
            body: "Samma scen ger både rörligt för kampanj och still-renders i tryckupplösning för sajt och katalog.",
          },
        ]}
        sections={[
          {
            id: "nar",
            heading: "När produktvisualisering slår produktfoto",
            body: (
              <p>
                När produkten fortfarande är en prototyp. När den är för stor, för tung eller för
                dyr att flytta till studio. När insidan är poängen och ni inte kan klyva en riktig
                enhet. När ni har trettio varianter av samma produkt. Och när ni behöver samma
                produkt i tio olika miljöer utan att resa till någon av dem.
              </p>
            ),
          },
          {
            id: "process",
            heading: "Så går en 3D-produktion till",
            body: (
              <p>
                CAD-granskning och omfångsmöte. Modelloptimering och materialsättning. Grå
                testrenders för vinklar och komposition — här styr ni innan tiden går åt. Ljus,
                textur och rendering. Compositing, grade och leverans i alla format ni behöver.
              </p>
            ),
          },
          {
            id: "leverans",
            heading: "Vad ni får ut",
            body: (
              <p>
                Master-film, kanalversioner (16:9, 9:16, 1:1), loopar för mässmonter, still-renders
                i webb- och tryckupplösning samt sparade scener för framtida revisioner. Behöver ni
                längre berättande produktion gör vi den som{" "}
                <a
                  className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                  href="/3d-animation"
                >
                  3D-animation
                </a>{" "}
                eller{" "}
                <a
                  className="underline decoration-mint/40 underline-offset-4 hover:text-mint"
                  href="/produktfilm"
                >
                  filmad produktfilm
                </a>
                .
              </p>
            ),
          },
        ]}
        faq={FAQ}
        caseLimit={4}
        closing={{
          title: "Har ni CAD-filer liggande?",
          body: "Skicka dem så säger vi vad som går att göra, vad det kostar och hur lång tid det tar.",
          primary: { label: "Skicka er CAD", to: "/kontakt" },
          secondary: { label: "Räkna pris", to: "/priser" },
        }}
      />
      <RelatedKnowledgeAuto topics={["3d-animation", "motion-graphics", "produktfilm", "pris"]} />
    </>
  ),
});
